import { useRef, useState } from "react";
import { Camera, Image as ImageIcon, RotateCcw, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { REMOTE_SURVEY, type CapturePath, type PhotoType } from "@/constants/remoteSurvey";
import type { RemoteSurveyImage } from "@/api/types";
import { ApiError } from "@/api/client";
import { formatSurveyPhotoError, inspectPhotoFile, photoUploadRequirements } from "@/utils/surveyPhoto";

type Props = {
  photoType: PhotoType;
  image?: RemoteSurveyImage;
  remaining: number;
  requiredCount: number;
  disabled?: boolean;
  onUpload: (file: File, capturePath: CapturePath) => Promise<void>;
  onRetake: () => Promise<void>;
};

function exampleLabel(photoType: PhotoType): string {
  return REMOTE_SURVEY.photoTypeLabels[photoType] ?? photoType;
}

export default function SurveyPhotoSlot({
  photoType,
  image,
  remaining,
  requiredCount,
  disabled,
  onUpload,
  onRetake,
}: Props) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pathUsed, setPathUsed] = useState<CapturePath | null>(null);

  const label = exampleLabel(photoType);
  const instruction = REMOTE_SURVEY.photoTypeInstructions[photoType];

  const handleFile = async (file: File | undefined, capturePath: CapturePath) => {
    if (!file) return;
    setError(null);
    const clientReason = await inspectPhotoFile(file);
    if (clientReason) {
      setError(formatSurveyPhotoError(clientReason));
      return;
    }
    setBusy(true);
    try {
      await onUpload(file, capturePath);
      setPathUsed(capturePath);
    } catch (err) {
      setError(formatSurveyPhotoError(err));
    } finally {
      setBusy(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#132035] p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-white">{label}</p>
          <p className="text-xs text-white/50 mt-1">{instruction}</p>
          <p className="text-xs text-white/40 mt-1">{photoUploadRequirements()}</p>
        </div>
        <span className="text-[11px] text-white/40 shrink-0">
          {remaining} of {requiredCount} remaining
        </span>
      </div>

      <div className="flex h-28 items-center justify-center rounded-lg border border-dashed border-white/15 bg-[#0d1b2e] overflow-hidden">
        {image?.url ? (
          <img src={image.url} alt={label} className="h-full w-full object-cover" />
        ) : (
          <div className="text-center px-3">
            <p className="text-[11px] uppercase tracking-wider text-white/35">Example</p>
            <p className="text-xs text-white/55 mt-1">{label}</p>
          </div>
        )}
      </div>

      {image ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-[#29ffa9] flex items-center gap-1.5">
            <CheckCircle className="h-3.5 w-3.5" />
            Photo accepted
            {pathUsed || image.capturePath ? ` · ${pathUsed ?? image.capturePath}` : ""}
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-white/20 text-white/70"
            disabled={disabled || busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onRetake();
              } catch (err) {
                setError(err instanceof ApiError ? err.message : "Could not clear this photo.");
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
            Retake
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            className="btn-cta"
            disabled={disabled || busy}
            onClick={() => cameraRef.current?.click()}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Camera className="h-4 w-4 mr-2" />}
            Camera
          </Button>
          <Button
            type="button"
            variant="outline"
            className="border-white/20 text-white/80"
            disabled={disabled || busy}
            onClick={() => galleryRef.current?.click()}
          >
            <ImageIcon className="h-4 w-4 mr-2" />
            Gallery
          </Button>
        </div>
      )}

      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-survey-capture="camera"
        disabled={disabled || busy}
        onChange={(e) => void handleFile(e.target.files?.[0], "camera")}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/jpeg,image/png,image/heic,image/heif"
        className="hidden"
        data-survey-capture="gallery"
        disabled={disabled || busy}
        onChange={(e) => void handleFile(e.target.files?.[0], "gallery")}
      />

      {error && (
        <p className="text-sm text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
